import { Divider, Grid, Stack, Typography } from "@mui/material";
import { type Currency } from "@prisma/client";
import classNames from "classnames";
import { type ComponentProps } from "react";
import { match } from "ts-pattern";
import { type Input as MarketDataInput } from "~/components/market-data/MarketDataBrowser";
import { LegacyTooltip } from "~/components/ui/core/LegacyTooltip";
import { LocationList } from "~/components/ui/LocationList";
import { formatMeasure } from "~/components/ui/MeasureSelector";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useMoney } from "~/hooks/useMoney";
import { useListJobsQuery } from "~/hooks/useQueries";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { formatPercent } from "~/lib/math";
import { useFetchAllowedCompanyBenchmarkLevelsQuery } from "~/pages/api/benchmark-level/fetch-allowed-company-benchmark-levels";
import { type Measure } from "~/services/employee";
import { type EmployeeLevel, formatBenchmarkLevel, formatLevel } from "~/services/employee/employeeLevel";
import { type MarketDataStats, MarketDataStatsSource } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

type MarketDataChartTooltipProps = React.PropsWithChildren<{
  useTestData: boolean;
  useBenchmarkLevelsInShare: boolean;
  level: number | EmployeeLevel;
  currency: Currency;
  color: Colors;
  measure: Measure;
  percentiles: PercentileBounds;
  input: MarketDataInput["market"];
  stats: MarketDataStats;
  disabled?: boolean;
  placement?: ComponentProps<typeof LegacyTooltip>["placement"];
}>;

const COLORS = ["primary", "secondary", "blue"] as const;
type Colors = (typeof COLORS)[number];

export const MarketDataChartTooltip: React.FC<MarketDataChartTooltipProps> = ({
  useTestData,
  useBenchmarkLevelsInShare,
  children,
  color,
  input,
  level,
  currency,
  measure,
  stats,
  percentiles,
  disabled = false,
  placement,
}) => {
  const { t } = useI18n();
  const { user } = useSession();
  const money = useMoney(currency);

  const { data: jobsData } = useListJobsQuery();
  const firstJob = jobsData?.jobs.find((job) => input.jobIds[0] === `${job.id}`);
  const { CAN_ACCESS_LEVEL_FRAMEWORKS } = useFeatureFlags();

  const canAccessBenchmarkLevels = CAN_ACCESS_LEVEL_FRAMEWORKS || useBenchmarkLevelsInShare;

  const { data: allowedBenchmarkLevels } = useFetchAllowedCompanyBenchmarkLevelsQuery();

  const { compensationValues, genderValues } = match(measure)
    .with("baseSalary", () => {
      return {
        compensationValues: stats.baseSalary,
        genderValues: stats.source === MarketDataStatsSource.DATASET ? stats.genderEquality?.baseSalary : null,
      };
    })
    .with("onTargetEarnings", () => {
      return {
        compensationValues: stats.onTargetEarnings,
        genderValues: stats.source === MarketDataStatsSource.DATASET ? stats.genderEquality?.onTargetEarnings : null,
      };
    })
    .with("totalCash", () => {
      return {
        compensationValues: stats.totalCash,
        genderValues: stats.source === MarketDataStatsSource.DATASET ? stats.genderEquality?.totalCash : null,
      };
    })
    .otherwise(() => {
      return { compensationValues: null, genderValues: null };
    });

  const computedPercentiles = {
    low: Math.round(percentiles.low * 100),
    middle: Math.round(percentiles.middle * 100),
    high: Math.round(percentiles.high * 100),
  };

  const captionColor = match(color)
    .with("primary", () => "primary.600")
    .with("secondary", () => "secondary.600")
    .with("blue", () => "info.600")
    .exhaustive();

  return (
    <LegacyTooltip
      placement={placement ?? "auto"}
      maxHeight="100vh"
      className="flex-auto"
      delay={100}
      disabled={disabled}
      content={
        <Stack className="w-[480px]">
          <Stack
            className={classNames("p-4", {
              "bg-primary-100": color === "primary",
              "bg-secondary-100": color === "secondary",
              "bg-blue-100": color === "blue",
            })}
          >
            <Typography variant="caption" color={captionColor}>
              {t("pages.market-data.market-data-chart-tooltip.market-data")}
            </Typography>

            <Stack direction="row" spacing={2}>
              <Typography noWrap variant="subtitle2">
                {firstJob?.name} {input.jobIds.length > 1 && `(+${input.jobIds.length - 1})`}
              </Typography>

              <Typography color="gray.600">·</Typography>

              <Typography color="gray.600">
                {!canAccessBenchmarkLevels
                  ? formatLevel(t, level as EmployeeLevel, user?.company.useAdvancedLevels ?? true)
                  : allowedBenchmarkLevels && formatBenchmarkLevel(level as number, allowedBenchmarkLevels)}
              </Typography>

              <Typography color="gray.600">·</Typography>

              <LocationList locations={input.locations} className="flex-nowrap overflow-hidden" />
            </Stack>
          </Stack>

          {useTestData && (
            <Stack className="bg-secondary-500 px-2">
              <Typography variant="subtitle2" color="common.white">
                {t("pages.market-data.market-data-chart-tooltip.this-is-dummy-compensation-data")}
              </Typography>
            </Stack>
          )}

          {compensationValues && (
            <Stack
              className={classNames("p-4", {
                "border-b border-gray-200": stats.source === MarketDataStatsSource.DATASET,
              })}
              spacing={2}
            >
              <Typography variant="subtitle2">{formatMeasure(t, measure)}</Typography>

              <Grid container rowGap={1}>
                <Grid item md={4}>
                  <Typography variant="subtitle2" color="gray.600">
                    {t("common.th", { percentile: computedPercentiles.low.toString() })}
                  </Typography>
                </Grid>

                <Grid item md={4}>
                  <Typography variant="subtitle2" color="gray.600">
                    {t("common.th", { percentile: computedPercentiles.middle.toString() })}
                  </Typography>
                </Grid>

                <Grid item md={4}>
                  <Typography variant="subtitle2" color="gray.600">
                    {t("common.th", { percentile: computedPercentiles.high.toString() })}
                  </Typography>
                </Grid>

                <Grid item md={12}>
                  <Divider light />
                </Grid>

                <Grid item md={4}>
                  <Typography color="text.secondary">
                    {money.format(compensationValues.low.value, { roundTo: 100 })}
                  </Typography>
                </Grid>

                <Grid item md={4}>
                  <Typography color="text.secondary">
                    {money.format(compensationValues.middle.value, { roundTo: 100 })}
                  </Typography>
                </Grid>

                <Grid item md={4}>
                  <Typography color="text.secondary">
                    {money.format(compensationValues.high.value, { roundTo: 100 })}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          )}

          {stats.source === MarketDataStatsSource.DATASET && (
            <>
              <Stack className="border-b border-gray-200 p-4" spacing={2}>
                <Typography variant="subtitle2">
                  {t("pages.market-data.market-data-chart-tooltip.variable-compensation")}
                </Typography>

                <Grid container rowGap={2}>
                  <Grid item md={6}>
                    <Typography color="gray.600">
                      {t("pages.market-data.market-data-chart-tooltip.companies-providing-bonuses")}
                    </Typography>
                  </Grid>

                  <Grid item md={6}>
                    <Typography color="text.secondary">
                      {formatPercent(stats.onTargetBonus.companiesPercentage)}
                    </Typography>
                  </Grid>

                  {stats.onTargetBonus.medianAmount !== null && (
                    <>
                      <Grid item md={6}>
                        <Typography color="gray.600">
                          {t("pages.market-data.market-data-chart-tooltip.median-bonus")}
                        </Typography>
                      </Grid>

                      <Grid item md={6}>
                        <Typography color="text.secondary">
                          {money.format(stats.onTargetBonus.medianAmount, { roundTo: 100 })}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {stats.onTargetBonus.baseSalaryProportion && (
                    <>
                      <Grid item md={6}>
                        <Typography color="gray.600">
                          {t("pages.market-data.market-data-chart-tooltip.bonus-as-percent")}
                        </Typography>
                      </Grid>

                      <Grid item md={6}>
                        <Typography color="text.secondary">
                          {formatPercent(stats.onTargetBonus.baseSalaryProportion)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Stack>

              {stats.genderEquality && (
                <Stack className="border-b border-gray-200 p-4" spacing={2}>
                  <Typography variant="subtitle2">
                    {t("pages.market-data.market-data-chart-tooltip.gender-equality-insights")}
                  </Typography>

                  <Grid container rowGap={1}>
                    <Grid item md={6}>
                      <Typography color="gray.600">
                        {t("pages.market-data.market-data-chart-tooltip.men-median")}
                      </Typography>
                    </Grid>

                    <Grid item md={6}>
                      <Typography color="gray.600">
                        {t("pages.market-data.market-data-chart-tooltip.women-median")}
                      </Typography>
                    </Grid>

                    <Grid item md={12}>
                      <Divider light />
                    </Grid>

                    <Grid item md={6}>
                      <Typography color="text.secondary">
                        {genderValues?.menMedian ? money.format(genderValues?.menMedian, { roundTo: 100 }) : "-"}
                      </Typography>
                    </Grid>

                    <Grid item md={6}>
                      <Typography color="text.secondary">
                        {genderValues?.womenMedian ? money.format(genderValues?.womenMedian, { roundTo: 100 }) : "-"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container rowGap={2}>
                    {stats.genderEquality && stats.genderEquality.womenProportion !== null && (
                      <>
                        <Grid item md={6}>
                          <Typography color="gray.600">
                            {t("pages.market-data.market-data-chart-tooltip.proportion-of-women")}
                          </Typography>
                        </Grid>
                        <Grid item md={6}>
                          <Typography color="text.secondary">
                            {formatPercent(stats.genderEquality.womenProportion)}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {stats.genderEquality && genderValues && genderValues.payGap !== null && (
                      <>
                        <Grid item md={6}>
                          <Typography color="gray.600">
                            {t("pages.market-data.market-data-chart-tooltip.pay-gap")}
                          </Typography>
                        </Grid>
                        <Grid item md={6}>
                          <Typography color="text.secondary">
                            {formatPercent(Math.abs(genderValues.payGap), 2)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Stack>
              )}
            </>
          )}
        </Stack>
      }
    >
      {children}
    </LegacyTooltip>
  );
};
